import { Component, DestroyRef, OnInit, OnDestroy, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormGroup, FormControl, Validators } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { Textarea } from 'primeng/textarea';
import { InputNumber } from 'primeng/inputnumber';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { Toast } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { Subject, filter } from 'rxjs';
import { FilingFormStateService } from '../../../services/mbrs/filing-form-state.service';
import { CrossReferenceService } from '../../../services/mbrs/cross-reference.service';
import { CalculationService } from '../../../services/mbrs/calculation.service';
import { CrossTabLayoutChangeNotifierService } from '../../../services/mbrs/cross-tab-layout-change-notifier.service';
import { NegativeHighlightDirective } from '../../../directives/negative-highlight.directive';
import { SchemaNumericDirective } from '../../../directives/schema-numeric.directive';
import { NumericFieldPolicy, resolveNumericPolicy } from '../../../utils/numeric-policy.util';
import { Router } from '@angular/router';
import { ReloadPromptComponent } from '../../common/reload-prompt/reload-prompt.component';
import { ReloadBannerComponent } from '../../common/reload-banner/reload-banner.component';
import { stripAllValidatorsRecursively } from '../../../utils/form-validation.util';
import {
  FORM_CALCULATION_DEBOUNCE_MS,
} from '../../../utils/debounce-times.util';
import { buildFormEditPipeline } from '../../../utils/form-edit-pipeline.util';
import { mapSchemaValidators } from '../../../utils/schema-validators.util';
import {
  resolveRole10LayoutFlags,
  resolveRole10VisibleSuffixes,
} from '../../../services/mbrs/validators/role10-sofp-liquidity.rules';
import { SHEET_KEYS } from '../../../models/sheet-keys';
import { ExtractedSumWarningService, EXTRACTED_SUM_MARKER } from '../../../services/mbrs/extracted-sum-warning.service';
import { SummableControl } from '../../../services/mbrs/extracted-sum-warning';
import { ExtractedSumBaselineDebugService } from '../../../services/common/extracted-sum-baseline-debug.service';
import {
  TOAST_DURATION_DEFAULT_MS,
  TOAST_DURATION_LONG_MS,
} from '../../../utils/toast-durations.util';
import { PlaceholderPipe } from '../../../utils/pipes/placeholder.pipe';
import { isControlMandatory } from '../../../utils/required-field.util';
import { RequiredMarkerComponent } from '../../common/required-marker/required-marker.component';

import { SheetLoaderComponent } from '../../common/sheet-loader/sheet-loader.component';
import { FieldLinkTargetDirective } from '../../../directives/field-link-target.directive';
import { computedControlPredicate, revealValidationErrors } from '../../../utils/validation-reveal.util';
import { adoptIntoAutoSaveSheet } from '../../../utils/adopt-remote-sheet-state.util';
import {
  LoadTrace,
  NO_OP_LOAD_TRACE,
  SheetLoadTraceService,
} from '../../../services/mbrs/sheet-load-trace.service';
import { SHEET_LOAD_STEPS } from '../../../utils/sheet-load-trace.util';
import {
  classifySheetInitError,
  fetchSheetSchema,
} from '../../../utils/schema-asset.util';
interface FormSchema {
  role: string;
  definition: string;
  sheetId: string;
  label: string;
  controls: FormControlSchema[];
}

interface FormControlSchema {
  qname: string;
  label: string;
  type: string;
  ui: {
    control: string;
    placeholder?: string;
    enumSource?: string;
    readonly?: boolean;
    calculated?: boolean;
    defaultValue?: string;
    rows?: number;
    level?: string;
    dualColumn?: boolean; // NEW: Indicates dual-column support
  };
  required?: boolean;
  validators?: Array<{ name: string; value?: number }>;
}

@Component({
  selector: 'app-role10-220000-statement-of-financial-position-liquidity',
  standalone: true,
  imports: [
    SheetLoaderComponent,
    RequiredMarkerComponent,
    CommonModule,
    ReactiveFormsModule,
    CardModule,
    InputTextModule,
    Textarea,
    InputNumber,
    ButtonModule,
    TooltipModule,
    Toast,
    NegativeHighlightDirective,

    SchemaNumericDirective,
    ReloadPromptComponent,
    ReloadBannerComponent,
    PlaceholderPipe,
    FieldLinkTargetDirective
  ],
  providers: [MessageService],
  templateUrl: './role10-220000-statement-of-financial-position-liquidity.component.html',
  styleUrls: ['./role10-220000-statement-of-financial-position-liquidity.component.css']
})
export class Role10220000StatementOfFinancialPositionLiquidityComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private destroyRef = inject(DestroyRef);
  private stateService = inject(FilingFormStateService);
  private crossRefService = inject(CrossReferenceService);
  private calculationService = inject(CalculationService);
  private layoutChangeNotifier = inject(CrossTabLayoutChangeNotifierService);
  private messageService = inject(MessageService);
  private loadTrace = inject(SheetLoadTraceService);
  /**
   * This mount's load trace. Opened as the first act of `ngOnInit` and closed
   * either by the terminal step mark or by the init catch, so a failure reports
   * which of the seven load steps this sheet actually reached.
   *
   * @see src/app/utils/sheet-load-trace.util.ts
   */
  private loadTraceHandle: LoadTrace = NO_OP_LOAD_TRACE;
  private extractedSumWarning = inject(ExtractedSumWarningService);
  private baselineDebug = inject(ExtractedSumBaselineDebugService);
  private validationErrors = new Map<string, string[]>();

  /** Word-suffix -> human label for the Error Description column. */
  private readonly ROLE10_SUFFIX_LABELS: { key: string; label: string }[] = [
    { key: 'GroupCurrent', label: 'Group Current' },
    { key: 'GroupPrevious', label: 'Group Previous' },
    { key: 'CompanyCurrent', label: 'Company Current' },
    { key: 'CompanyPrevious', label: 'Company Previous' },
    { key: 'Current', label: 'Current' },
    { key: 'Previous', label: 'Previous' },
  ];

  private readonly sheetKey = SHEET_KEYS.statementOfFinancialPositionLiquidity;

  statementOfFinancialPositionLiquidityForm!: FormGroup;
  schema!: FormSchema;
  isLoading = true;
  /**
   * Card header carrying the filing's submission type, e.g.
   * "Statement of Financial Position (Liquidity Order) (FS-MPERS)".
   *
   * Two rules, both of which this sheet used to break:
   *  - the name comes from the canonical `SHEET_DISPLAY_NAMES` map (via
   *    `getSheetTitleWithSubmissionType`), never a literal repeated here;
   *  - it is a LIVE getter, never an `ngOnInit` snapshot — see the cold-mount
   *    rule on `FilingFormStateService.getFormTitleWithSubmissionType()`.
   */
  get formTitle(): string {
    return this.stateService.getSheetTitleWithSubmissionType(
      SHEET_KEYS.statementOfFinancialPositionLiquidity
    );
  }

  // Layout flags sourced from Role-2 (scopeOfFiling) at init. Under the
  // reload-prompt model these are READ ONCE and never mutated after
  // buildForm() — an upstream change surfaces a reload prompt rather than
  // rewriting the live FormGroup.
  isConsolidated = false;
  hasPreviousYear = false;

  // Reload-prompt state. showReloadPrompt gates the modal visibility;
  // reloadPromptDismissed is a one-way latch — once true, it stays true
  // for the rest of the page lifetime and the banner + disabled-submit +
  // disabled-validations side-effects take over.
  showReloadPrompt = signal<boolean>(false);
  reloadPromptDismissed = signal<boolean>(false);

  /**
   * Get currency symbol with unit from Role-1
   * e.g., "RM'000", "$'000", "€'000"
   */
  get currencyUnit(): string {
    const symbol = this.crossRefService.getCurrencySymbol();
    return `${symbol}'000`;
  }

  /**
   * Get period header for current year
   * e.g., "For the year ended 31 December 2024"
   */
  get periodHeaderCurrent(): string {
    return this.crossRefService.getPeriodHeader('current');
  }

  /**
   * Get period header for previous year
   * e.g., "For the year ended 31 December 2023"
   */
  get periodHeaderPrevious(): string {
    return this.crossRefService.getPeriodHeader('previous');
  }

  private isLoadingState = false;
  private isCalculating = false;

  async ngOnInit(): Promise<void> {
    // Open this mount's load trace before anything else, so a failure
    // anywhere below reports which load steps were reached.
    this.loadTraceHandle = this.loadTrace.beginSheetLoad(
      '10',
      this.stateService.getConversionId(),
      this.destroyRef,
    );
    // This sheet does not await the container's MBRS hydration, so step 1
    // has nothing to wait for and is satisfied on arrival.
    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.hydrationSettled);
    this.baselineDebug.bindGlobalShortcut(this.destroyRef);
    try {
      // NEW: Check consolidated status from Role-2
      this.checkConsolidatedStatus();

      await this.loadSchema();
      this.buildForm();
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.formBuilt);
      this.setupEditPipeline();

      // Reveal the sheet's already-computed errors now that the form exists
      // and has been populated. Deterministic replacement for the filing-form
      // container's setTimeout-driven markAllAsTouched, which silently no-ops
      // when a sheet's async init has not yet assigned its FormGroup — see
      // revealValidationErrors' docblock. Touch-only: cannot move the verdict.
      revealValidationErrors(this.statementOfFinancialPositionLiquidityForm, {
        isComputed: computedControlPredicate(this.schema?.controls),
      });
      this.loadSavedState();
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.stateRestored);
      this.setupLayoutReactivity();
      this.setupCrossTabLiveAdoption();
      this.isLoading = false;
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.finalized);
    } catch (error) {
      // Report through the trace instead of a fixed sentence. The toast now
      // names this sheet, the steps it reached, and whether the schema was
      // missing, unreadable, or fine but the form could not be built —
      // `classifySheetInitError` infers the last of those by elimination.
      // Must run BEFORE the loading flag is cleared below: `fail()` closes the
      // trace, which is what stops the finalize mark on the next line from
      // recording this failed init as a success.
      this.loadTraceHandle.fail(classifySheetInitError(error), error);
      this.isLoading = false;
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.finalized);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Read scopeOfFiling from the current modified state and cache the
   * layout flags on the component. Called ONCE during ngOnInit — after
   * initialization the flags are frozen; any upstream change is handled
   * by the reload-prompt path (see setupLayoutReactivity).
   */
  private checkConsolidatedStatus(): void {
    const scopeOfFilingData = this.stateService.getSheetData(SHEET_KEYS.scopeOfFiling);
    const flags = resolveRole10LayoutFlags(scopeOfFilingData);
    this.isConsolidated = flags.isConsolidated;
    this.hasPreviousYear = flags.hasPreviousYear;
  }

  // Why: pure predicate, no mutation of isConsolidated / hasPreviousYear.
  // Under the reload-prompt model the component's layout MUST stay frozen
  // until the user reloads — mutating the flags here would break the
  // schema/data invariant the cached FormGroup relies on. Compare-only,
  // return boolean, let the notifier surface the prompt.
  //
  // Layout derivation is shared with the pure validator via
  // `resolveRole10LayoutFlags()` so both paths see the same scopeOfFiling
  // → (isConsolidated, hasPreviousYear) projection.
  private detectLayoutFlagsChanged(): boolean {
    const scopeOfFilingData = this.stateService.getSheetData(SHEET_KEYS.scopeOfFiling);
    const fresh = resolveRole10LayoutFlags(scopeOfFilingData);

    return (
      fresh.isConsolidated !== this.isConsolidated ||
      fresh.hasPreviousYear !== this.hasPreviousYear
    );
  }

  private async loadSchema(): Promise<void> {
    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.schemaSelected);
    this.schema = await fetchSheetSchema<FormSchema>(
      this.loadTraceHandle,
      '/assets/forms/role-220000.form.json',
    );
    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.schemaParsed);
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('Schema loaded:', this.schema);
  }

  /**
   * NEW: Build the reactive form from schema with dual-column support
   */
  private buildForm(): void {
    if (!this.schema || !this.schema.controls) {
      console.error('Schema not loaded - cannot build form');
      throw new Error('Schema must be loaded before building form');
    }

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('Building form with schema:', this.schema);
    const formControls: { [key: string]: FormControl<any> } = {};

    this.schema.controls.forEach((control: FormControlSchema) => {
      // Skip section headers
      if (control.type === 'SectionHeader') {
        return;
      }

      const validators = [];

      // Add required validator
      if (control.required) {
        validators.push(Validators.required);
      }

      // Numeric/format constraints (min/max/pattern) from the schema, derived
      // per control via the shared util — see schema-validators.util.
      validators.push(...mapSchemaValidators(control));

      // Get default value
      let defaultValue: any = null;
      if (control.ui.defaultValue) {
        defaultValue = control.ui.defaultValue;
      }

      // NEW: Create controls for monetary fields with dynamic column support
      if (control.ui.dualColumn) {
        if (this.isConsolidated && this.hasPreviousYear) {
          // 4 columns: Group Current, Group Previous, Company Current, Company Previous
          formControls[control.qname + 'GroupCurrent'] = new FormControl(
            { value: defaultValue, disabled: control.ui.readonly || control.ui.calculated || false },
            validators
          );
          formControls[control.qname + 'GroupPrevious'] = new FormControl(
            { value: defaultValue, disabled: control.ui.readonly || control.ui.calculated || false },
            validators
          );
          formControls[control.qname + 'CompanyCurrent'] = new FormControl(
            { value: defaultValue, disabled: control.ui.readonly || control.ui.calculated || false },
            validators
          );
          formControls[control.qname + 'CompanyPrevious'] = new FormControl(
            { value: defaultValue, disabled: control.ui.readonly || control.ui.calculated || false },
            validators
          );
        } else if (this.isConsolidated && !this.hasPreviousYear) {
          // 2 columns: Group Current, Company Current
          formControls[control.qname + 'GroupCurrent'] = new FormControl(
            { value: defaultValue, disabled: control.ui.readonly || control.ui.calculated || false },
            validators
          );
          formControls[control.qname + 'CompanyCurrent'] = new FormControl(
            { value: defaultValue, disabled: control.ui.readonly || control.ui.calculated || false },
            validators
          );
        } else if (!this.isConsolidated && this.hasPreviousYear) {
          // 2 columns: Current Year, Previous Year
          formControls[control.qname + 'Current'] = new FormControl(
            { value: defaultValue, disabled: control.ui.readonly || control.ui.calculated || false },
            validators
          );
          formControls[control.qname + 'Previous'] = new FormControl(
            { value: defaultValue, disabled: control.ui.readonly || control.ui.calculated || false },
            validators
          );
        } else {
          // 1 column: Current Year only
          formControls[control.qname + 'Current'] = new FormControl(
            { value: defaultValue, disabled: control.ui.readonly || control.ui.calculated || false },
            validators
          );
        }
      } else {
        // Single control for non-monetary fields
        const formControl = new FormControl(
          { value: defaultValue, disabled: control.ui.readonly || control.ui.calculated || false },
          validators
        );
        formControls[control.qname] = formControl;
      }
    });

    this.statementOfFinancialPositionLiquidityForm = new FormGroup(formControls);
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('Form built with controls:', Object.keys(formControls));

    // Disable calculated fields to prevent manual editing
    this.disableCalculatedFields();
  }

  /**
   * Disable calculated fields programmatically
   * Called after form creation to prevent "changed after checked" warnings
   */
  private disableCalculatedFields(): void {
    if (!this.schema) return;

    this.schema.controls.forEach(control => {
      if (this.isCalculatedField(control)) {
        // Disable all column variants for this calculated field
        if (this.isConsolidated && this.hasPreviousYear) {
          this.statementOfFinancialPositionLiquidityForm.get(control.qname + 'GroupCurrent')?.disable({ emitEvent: false });
          this.statementOfFinancialPositionLiquidityForm.get(control.qname + 'GroupPrevious')?.disable({ emitEvent: false });
          this.statementOfFinancialPositionLiquidityForm.get(control.qname + 'CompanyCurrent')?.disable({ emitEvent: false });
          this.statementOfFinancialPositionLiquidityForm.get(control.qname + 'CompanyPrevious')?.disable({ emitEvent: false });
        } else if (this.isConsolidated && !this.hasPreviousYear) {
          this.statementOfFinancialPositionLiquidityForm.get(control.qname + 'GroupCurrent')?.disable({ emitEvent: false });
          this.statementOfFinancialPositionLiquidityForm.get(control.qname + 'CompanyCurrent')?.disable({ emitEvent: false });
        } else if (!this.isConsolidated && this.hasPreviousYear) {
          this.statementOfFinancialPositionLiquidityForm.get(control.qname + 'Current')?.disable({ emitEvent: false });
          this.statementOfFinancialPositionLiquidityForm.get(control.qname + 'Previous')?.disable({ emitEvent: false });
        } else {
          this.statementOfFinancialPositionLiquidityForm.get(control.qname + 'Current')?.disable({ emitEvent: false });
        }
      }
    });
  }

  /**
   * Wire the single debounced edit pipeline: calc → persist (no separate validate).
   *
   * Bound to `destroyRef` via takeUntilDestroyed — same lifetime as the form
   * which is built ONCE at init and never rebuilt mid-life.
   */
  private setupEditPipeline(): void {
    buildFormEditPipeline({
      source$: this.statementOfFinancialPositionLiquidityForm.valueChanges,
      debounceMs: FORM_CALCULATION_DEBOUNCE_MS,
      destroyRef: this.destroyRef,
      shouldSkip: () => this.isLoadingState || this.isCalculating,
      calculate: () => this.calculateTotals(),
      persist: () => this.stateService.updateSheetData(
        SHEET_KEYS.statementOfFinancialPositionLiquidity,
        this.statementOfFinancialPositionLiquidityForm.value,
        true
      ),
    });
  }

  /**
   * NEW: Calculate all totals in the statement (all column combinations)
   */
  private calculateTotals(): void {
    this.isCalculating = true;

    // Determine suffixes based on consolidation and comparative status.
    // Shared with the pure validator via `resolveRole10VisibleSuffixes` so
    // both paths derive from the same scopeOfFiling projection.
    const suffixes = resolveRole10VisibleSuffixes(
      this.stateService.getSheetData(SHEET_KEYS.scopeOfFiling)
    );

    const calculatedTotals = this.calculationService.calculateTotals_220000(
      this.statementOfFinancialPositionLiquidityForm.getRawValue(),
      suffixes
    );

    Object.keys(calculatedTotals).forEach((controlName) => {
      this.statementOfFinancialPositionLiquidityForm
        .get(controlName)
        ?.setValue(calculatedTotals[controlName], { emitEvent: false });
    });

    this.stateService.registerCalculatedKeys(
      SHEET_KEYS.statementOfFinancialPositionLiquidity,
      Object.keys(calculatedTotals)
    );

    this.validateAgainstApiData();

    this.isCalculating = false;
  }

  /**
   * Load saved state from state service
   */
  private loadSavedState(): void {
    this.isLoadingState = true;
    this.stateService.markSheetInitializing(SHEET_KEYS.statementOfFinancialPositionLiquidity);
    const savedData = this.stateService.getSheetData(SHEET_KEYS.statementOfFinancialPositionLiquidity);

    if (savedData && Object.keys(savedData).length > 0) {
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log('Loading saved state:', savedData);
      this.statementOfFinancialPositionLiquidityForm.patchValue(savedData, { emitEvent: false });

      // Trigger calculations after loading
      setTimeout(() => {
        this.calculateTotals();
        this.isLoadingState = false;

        // Snapshot baseline after all debounced valueChanges settle
        setTimeout(() => {
          this.stateService.markSheetInitializedWithStatus(SHEET_KEYS.statementOfFinancialPositionLiquidity, false);
        }, 600);
      }, 100);
    } else {
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log('No saved state found');
      this.isLoadingState = false;

      // Snapshot baseline after all debounced valueChanges settle
      setTimeout(() => {
        this.stateService.markSheetInitializedWithStatus(SHEET_KEYS.statementOfFinancialPositionLiquidity, false);
      }, 600);
    }
  }

  /**
   * Cross-tab + same-tab reactivity for scopeOfFiling changes.
   *
   * role10's column layout (1/2/4 columns) is derived from Role-0
   * (filingPrerequisites) and Role-2 (scopeOfFiling) at buildForm() time.
   * Once the form is built it stays frozen: any upstream edit surfaces
   * the shared reload prompt and — if dismissed — latches the component
   * into a degraded state (submit disabled, validations cleared,
   * persistent banner) until the user reloads.
   */
  private router = inject(Router);

  private setupLayoutReactivity(): void {
    this.layoutChangeNotifier
      .watchSubmissionCommitted({
        sheetKey: this.sheetKey,
        slicesOfInterest: [SHEET_KEYS.filingPrerequisites, SHEET_KEYS.filingInfo, SHEET_KEYS.scopeOfFiling],
        detectChanged: () => this.detectLayoutFlagsChanged(),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (!this.reloadPromptDismissed()) {
          this.showReloadPrompt.set(true);
        }
      });
  }

  /**
   * Cross-tab live adoption — when a sibling tab edits THIS sheet's own
   * statementOfFinancialPositionLiquidity slice, `applyRemoteChange`
   * rehydrates the shared state and publishes `changedSheets` on
   * `remoteUpdateApplied$`. `adoptRemoteState()` below repaints this
   * sheet's form from that state (silently, no emitted valueChanges).
   */
  private setupCrossTabLiveAdoption(): void {
    this.stateService.remoteUpdateApplied$
      .pipe(
        filter(({ streamKey }) => streamKey === 'modifiedState'),
        filter(({ changedSheets }) => changedSheets.includes(SHEET_KEYS.statementOfFinancialPositionLiquidity)),
        filter(() => !this.stateService.isSheetInitializing(SHEET_KEYS.statementOfFinancialPositionLiquidity)),
        filter(() => !!this.schema && !!this.statementOfFinancialPositionLiquidityForm),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.adoptRemoteState();
      });
  }

  /**
   * Cross-tab adoption: a sibling tab edited this sheet, `applyRemoteChange` has
   * already rehydrated the shared state, and this repaints the form from it.
   *
   * MUST NOT write back to state — see the invariant on `adoptIntoAutoSaveSheet`.
   * The validation tail below runs for DISPLAY only; the sibling already
   * persisted its own results.
   *
   * Developer Note (TODO): silent adoption is the P.O. decision of 2026-08-17 and
   * is revisitable. If feedback is later wanted, the two options considered were a
   * brief per-row highlight (preferred — it answers "why did that number change?"
   * at the spot it changed) and a per-adoption toast (cheaper, but says nothing
   * about WHAT changed and stacks on repeated sibling edits).
   */
  private adoptRemoteState(): void {
    adoptIntoAutoSaveSheet(
      this.statementOfFinancialPositionLiquidityForm,
      this.stateService.getSheetData(SHEET_KEYS.statementOfFinancialPositionLiquidity)
    );
    this.calculateTotals();
  }

  onReload(): void {
    window.location.reload();
  }

  onDismiss(): void {
    this.showReloadPrompt.set(false);
    this.reloadPromptDismissed.set(true);
    this.disableAllValidations();
  }

  onReturnToFiles(): void {
    this.showReloadPrompt.set(false);
    this.router.navigate(['/mbrs/xbrl-files']);
  }

  // Why: one-way door. Once the user dismisses the reload prompt the
  // in-memory form schema is known-stale vs. upstream scope; persisting
  // any further validation result would be misleading. We strip every
  // validator from every control (and the root FormGroup) so no spurious
  // errors accumulate before the user reloads. There is deliberately no
  // re-enable path — a fresh ngOnInit (via reload) is the only recovery.
  private disableAllValidations(): void {
    stripAllValidatorsRecursively(this.statementOfFinancialPositionLiquidityForm);
  }

  /**
   * NEW: Auto-fill demo data (all column combinations)
   */
  autoFillDemo(): void {
    const demoData: any = {
      'ssmt:StatementOfFinancialPositionLiquidityExplanatory': 'Statement of Financial Position presented by order of liquidity as at 31 December 2024'
    };

    if (this.isConsolidated && this.hasPreviousYear) {
      // 4 columns: Group Current, Group Previous, Company Current, Company Previous
      // Group Current Year
      Object.assign(demoData, {
        'ssmt:CashAndCashEquivalentsLiquidityGroupCurrent': 1500000,
        'ssmt:ShortTermInvestmentsLiquidityGroupCurrent': 350000,
        'ssmt:TradeReceivablesLiquidityGroupCurrent': 2000000,
        'ssmt:OtherReceivablesLiquidityGroupCurrent': 250000,
        'ssmt:InventoriesLiquidityGroupCurrent': 1600000,
        'ssmt:PrepaymentsLiquidityGroupCurrent': 80000,
        'ssmt:PropertyPlantAndEquipmentLiquidityGroupCurrent': 6000000,
        'ssmt:InvestmentPropertiesLiquidityGroupCurrent': 0,
        'ssmt:IntangibleAssetsLiquidityGroupCurrent': 400000,
        'ssmt:InvestmentsInSubsidiariesLiquidityGroupCurrent': 0,
        'ssmt:InvestmentsInAssociatesLiquidityGroupCurrent': 300000,
        'ssmt:InvestmentsInJointVenturesLiquidityGroupCurrent': 200000,
        'ssmt:LongTermInvestmentsLiquidityGroupCurrent': 150000,
        'ssmt:DeferredTaxAssetsLiquidityGroupCurrent': 120000,
        'ssmt:OtherAssetsLiquidityGroupCurrent': 40000,
        'ssmt:BankOverdraftsLiquidityGroupCurrent': 250000,
        'ssmt:TradePayablesLiquidityGroupCurrent': 1100000,
        'ssmt:OtherPayablesLiquidityGroupCurrent': 350000,
        'ssmt:ShortTermBorrowingsLiquidityGroupCurrent': 800000,
        'ssmt:CurrentTaxLiabilitiesLiquidityGroupCurrent': 200000,
        'ssmt:ShortTermProvisionsLiquidityGroupCurrent': 130000,
        'ssmt:LongTermBorrowingsLiquidityGroupCurrent': 2000000,
        'ssmt:DeferredTaxLiabilitiesLiquidityGroupCurrent': 180000,
        'ssmt:LongTermProvisionsLiquidityGroupCurrent': 120000,
        'ssmt:OtherLiabilitiesLiquidityGroupCurrent': 40000,
        'ssmt:ShareCapitalLiquidityGroupCurrent': 1000000,
        'ssmt:SharePremiumLiquidityGroupCurrent': 500000,
        'ssmt:RetainedEarningsLiquidityGroupCurrent': 3340000,
        'ssmt:OtherReservesLiquidityGroupCurrent': 350000,
        // Group Previous Year (10% lower)
        'ssmt:CashAndCashEquivalentsLiquidityGroupPrevious': 1350000,
        'ssmt:ShortTermInvestmentsLiquidityGroupPrevious': 315000,
        'ssmt:TradeReceivablesLiquidityGroupPrevious': 1800000,
        'ssmt:OtherReceivablesLiquidityGroupPrevious': 225000,
        'ssmt:InventoriesLiquidityGroupPrevious': 1440000,
        'ssmt:PrepaymentsLiquidityGroupPrevious': 72000,
        'ssmt:PropertyPlantAndEquipmentLiquidityGroupPrevious': 5400000,
        'ssmt:InvestmentPropertiesLiquidityGroupPrevious': 0,
        'ssmt:IntangibleAssetsLiquidityGroupPrevious': 360000,
        'ssmt:InvestmentsInSubsidiariesLiquidityGroupPrevious': 0,
        'ssmt:InvestmentsInAssociatesLiquidityGroupPrevious': 270000,
        'ssmt:InvestmentsInJointVenturesLiquidityGroupPrevious': 180000,
        'ssmt:LongTermInvestmentsLiquidityGroupPrevious': 135000,
        'ssmt:DeferredTaxAssetsLiquidityGroupPrevious': 108000,
        'ssmt:OtherAssetsLiquidityGroupPrevious': 36000,
        'ssmt:BankOverdraftsLiquidityGroupPrevious': 225000,
        'ssmt:TradePayablesLiquidityGroupPrevious': 990000,
        'ssmt:OtherPayablesLiquidityGroupPrevious': 315000,
        'ssmt:ShortTermBorrowingsLiquidityGroupPrevious': 720000,
        'ssmt:CurrentTaxLiabilitiesLiquidityGroupPrevious': 180000,
        'ssmt:ShortTermProvisionsLiquidityGroupPrevious': 117000,
        'ssmt:LongTermBorrowingsLiquidityGroupPrevious': 1800000,
        'ssmt:DeferredTaxLiabilitiesLiquidityGroupPrevious': 162000,
        'ssmt:LongTermProvisionsLiquidityGroupPrevious': 108000,
        'ssmt:OtherLiabilitiesLiquidityGroupPrevious': 36000,
        'ssmt:ShareCapitalLiquidityGroupPrevious': 1000000,
        'ssmt:SharePremiumLiquidityGroupPrevious': 500000,
        'ssmt:RetainedEarningsLiquidityGroupPrevious': 2839000,
        'ssmt:OtherReservesLiquidityGroupPrevious': 315000,
        // Company Current Year
        'ssmt:CashAndCashEquivalentsLiquidityCompanyCurrent': 850000,
        'ssmt:ShortTermInvestmentsLiquidityCompanyCurrent': 200000,
        'ssmt:TradeReceivablesLiquidityCompanyCurrent': 1200000,
        'ssmt:OtherReceivablesLiquidityCompanyCurrent': 150000,
        'ssmt:InventoriesLiquidityCompanyCurrent': 950000,
        'ssmt:PrepaymentsLiquidityCompanyCurrent': 50000,
        'ssmt:PropertyPlantAndEquipmentLiquidityCompanyCurrent': 3500000,
        'ssmt:InvestmentPropertiesLiquidityCompanyCurrent': 0,
        'ssmt:IntangibleAssetsLiquidityCompanyCurrent': 200000,
        'ssmt:InvestmentsInSubsidiariesLiquidityCompanyCurrent': 500000,
        'ssmt:InvestmentsInAssociatesLiquidityCompanyCurrent': 0,
        'ssmt:InvestmentsInJointVenturesLiquidityCompanyCurrent': 0,
        'ssmt:LongTermInvestmentsLiquidityCompanyCurrent': 100000,
        'ssmt:DeferredTaxAssetsLiquidityCompanyCurrent': 75000,
        'ssmt:OtherAssetsLiquidityCompanyCurrent': 25000,
        'ssmt:BankOverdraftsLiquidityCompanyCurrent': 150000,
        'ssmt:TradePayablesLiquidityCompanyCurrent': 650000,
        'ssmt:OtherPayablesLiquidityCompanyCurrent': 200000,
        'ssmt:ShortTermBorrowingsLiquidityCompanyCurrent': 500000,
        'ssmt:CurrentTaxLiabilitiesLiquidityCompanyCurrent': 120000,
        'ssmt:ShortTermProvisionsLiquidityCompanyCurrent': 80000,
        'ssmt:LongTermBorrowingsLiquidityCompanyCurrent': 1200000,
        'ssmt:DeferredTaxLiabilitiesLiquidityCompanyCurrent': 100000,
        'ssmt:LongTermProvisionsLiquidityCompanyCurrent': 75000,
        'ssmt:OtherLiabilitiesLiquidityCompanyCurrent': 25000,
        'ssmt:ShareCapitalLiquidityCompanyCurrent': 1000000,
        'ssmt:SharePremiumLiquidityCompanyCurrent': 500000,
        'ssmt:RetainedEarningsLiquidityCompanyCurrent': 1725000,
        'ssmt:OtherReservesLiquidityCompanyCurrent': 200000,
        // Company Previous Year (10% lower)
        'ssmt:CashAndCashEquivalentsLiquidityCompanyPrevious': 765000,
        'ssmt:ShortTermInvestmentsLiquidityCompanyPrevious': 180000,
        'ssmt:TradeReceivablesLiquidityCompanyPrevious': 1080000,
        'ssmt:OtherReceivablesLiquidityCompanyPrevious': 135000,
        'ssmt:InventoriesLiquidityCompanyPrevious': 855000,
        'ssmt:PrepaymentsLiquidityCompanyPrevious': 45000,
        'ssmt:PropertyPlantAndEquipmentLiquidityCompanyPrevious': 3150000,
        'ssmt:InvestmentPropertiesLiquidityCompanyPrevious': 0,
        'ssmt:IntangibleAssetsLiquidityCompanyPrevious': 180000,
        'ssmt:InvestmentsInSubsidiariesLiquidityCompanyPrevious': 450000,
        'ssmt:InvestmentsInAssociatesLiquidityCompanyPrevious': 0,
        'ssmt:InvestmentsInJointVenturesLiquidityCompanyPrevious': 0,
        'ssmt:LongTermInvestmentsLiquidityCompanyPrevious': 90000,
        'ssmt:DeferredTaxAssetsLiquidityCompanyPrevious': 67500,
        'ssmt:OtherAssetsLiquidityCompanyPrevious': 22500,
        'ssmt:BankOverdraftsLiquidityCompanyPrevious': 135000,
        'ssmt:TradePayablesLiquidityCompanyPrevious': 585000,
        'ssmt:OtherPayablesLiquidityCompanyPrevious': 180000,
        'ssmt:ShortTermBorrowingsLiquidityCompanyPrevious': 450000,
        'ssmt:CurrentTaxLiabilitiesLiquidityCompanyPrevious': 108000,
        'ssmt:ShortTermProvisionsLiquidityCompanyPrevious': 72000,
        'ssmt:LongTermBorrowingsLiquidityCompanyPrevious': 1080000,
        'ssmt:DeferredTaxLiabilitiesLiquidityCompanyPrevious': 90000,
        'ssmt:LongTermProvisionsLiquidityCompanyPrevious': 67500,
        'ssmt:OtherLiabilitiesLiquidityCompanyPrevious': 22500,
        'ssmt:ShareCapitalLiquidityCompanyPrevious': 1000000,
        'ssmt:SharePremiumLiquidityCompanyPrevious': 500000,
        'ssmt:RetainedEarningsLiquidityCompanyPrevious': 1372500,
        'ssmt:OtherReservesLiquidityCompanyPrevious': 180000
      });
    } else if (this.isConsolidated && !this.hasPreviousYear) {
      // 2 columns: Group Current, Company Current (no previous year)
      Object.assign(demoData, {
        // Group Current
        'ssmt:CashAndCashEquivalentsLiquidityGroupCurrent': 1500000,
        'ssmt:ShortTermInvestmentsLiquidityGroupCurrent': 350000,
        'ssmt:TradeReceivablesLiquidityGroupCurrent': 2000000,
        'ssmt:OtherReceivablesLiquidityGroupCurrent': 250000,
        'ssmt:InventoriesLiquidityGroupCurrent': 1600000,
        'ssmt:PrepaymentsLiquidityGroupCurrent': 80000,
        'ssmt:PropertyPlantAndEquipmentLiquidityGroupCurrent': 6000000,
        'ssmt:InvestmentPropertiesLiquidityGroupCurrent': 0,
        'ssmt:IntangibleAssetsLiquidityGroupCurrent': 400000,
        'ssmt:InvestmentsInSubsidiariesLiquidityGroupCurrent': 0,
        'ssmt:InvestmentsInAssociatesLiquidityGroupCurrent': 300000,
        'ssmt:InvestmentsInJointVenturesLiquidityGroupCurrent': 200000,
        'ssmt:LongTermInvestmentsLiquidityGroupCurrent': 150000,
        'ssmt:DeferredTaxAssetsLiquidityGroupCurrent': 120000,
        'ssmt:OtherAssetsLiquidityGroupCurrent': 40000,
        'ssmt:BankOverdraftsLiquidityGroupCurrent': 250000,
        'ssmt:TradePayablesLiquidityGroupCurrent': 1100000,
        'ssmt:OtherPayablesLiquidityGroupCurrent': 350000,
        'ssmt:ShortTermBorrowingsLiquidityGroupCurrent': 800000,
        'ssmt:CurrentTaxLiabilitiesLiquidityGroupCurrent': 200000,
        'ssmt:ShortTermProvisionsLiquidityGroupCurrent': 130000,
        'ssmt:LongTermBorrowingsLiquidityGroupCurrent': 2000000,
        'ssmt:DeferredTaxLiabilitiesLiquidityGroupCurrent': 180000,
        'ssmt:LongTermProvisionsLiquidityGroupCurrent': 120000,
        'ssmt:OtherLiabilitiesLiquidityGroupCurrent': 40000,
        'ssmt:ShareCapitalLiquidityGroupCurrent': 1000000,
        'ssmt:SharePremiumLiquidityGroupCurrent': 500000,
        'ssmt:RetainedEarningsLiquidityGroupCurrent': 3340000,
        'ssmt:OtherReservesLiquidityGroupCurrent': 350000,
        // Company Current
        'ssmt:CashAndCashEquivalentsLiquidityCompanyCurrent': 850000,
        'ssmt:ShortTermInvestmentsLiquidityCompanyCurrent': 200000,
        'ssmt:TradeReceivablesLiquidityCompanyCurrent': 1200000,
        'ssmt:OtherReceivablesLiquidityCompanyCurrent': 150000,
        'ssmt:InventoriesLiquidityCompanyCurrent': 950000,
        'ssmt:PrepaymentsLiquidityCompanyCurrent': 50000,
        'ssmt:PropertyPlantAndEquipmentLiquidityCompanyCurrent': 3500000,
        'ssmt:InvestmentPropertiesLiquidityCompanyCurrent': 0,
        'ssmt:IntangibleAssetsLiquidityCompanyCurrent': 200000,
        'ssmt:InvestmentsInSubsidiariesLiquidityCompanyCurrent': 500000,
        'ssmt:InvestmentsInAssociatesLiquidityCompanyCurrent': 0,
        'ssmt:InvestmentsInJointVenturesLiquidityCompanyCurrent': 0,
        'ssmt:LongTermInvestmentsLiquidityCompanyCurrent': 100000,
        'ssmt:DeferredTaxAssetsLiquidityCompanyCurrent': 75000,
        'ssmt:OtherAssetsLiquidityCompanyCurrent': 25000,
        'ssmt:BankOverdraftsLiquidityCompanyCurrent': 150000,
        'ssmt:TradePayablesLiquidityCompanyCurrent': 650000,
        'ssmt:OtherPayablesLiquidityCompanyCurrent': 200000,
        'ssmt:ShortTermBorrowingsLiquidityCompanyCurrent': 500000,
        'ssmt:CurrentTaxLiabilitiesLiquidityCompanyCurrent': 120000,
        'ssmt:ShortTermProvisionsLiquidityCompanyCurrent': 80000,
        'ssmt:LongTermBorrowingsLiquidityCompanyCurrent': 1200000,
        'ssmt:DeferredTaxLiabilitiesLiquidityCompanyCurrent': 100000,
        'ssmt:LongTermProvisionsLiquidityCompanyCurrent': 75000,
        'ssmt:OtherLiabilitiesLiquidityCompanyCurrent': 25000,
        'ssmt:ShareCapitalLiquidityCompanyCurrent': 1000000,
        'ssmt:SharePremiumLiquidityCompanyCurrent': 500000,
        'ssmt:RetainedEarningsLiquidityCompanyCurrent': 1725000,
        'ssmt:OtherReservesLiquidityCompanyCurrent': 200000
      });
    } else if (!this.isConsolidated && this.hasPreviousYear) {
      // 2 columns: Current, Previous (no consolidation)
      Object.assign(demoData, {
        // Current Year
        'ssmt:CashAndCashEquivalentsLiquidityCurrent': 850000,
        'ssmt:ShortTermInvestmentsLiquidityCurrent': 200000,
        'ssmt:TradeReceivablesLiquidityCurrent': 1200000,
        'ssmt:OtherReceivablesLiquidityCurrent': 150000,
        'ssmt:InventoriesLiquidityCurrent': 950000,
        'ssmt:PrepaymentsLiquidityCurrent': 50000,
        'ssmt:PropertyPlantAndEquipmentLiquidityCurrent': 3500000,
        'ssmt:InvestmentPropertiesLiquidityCurrent': 0,
        'ssmt:IntangibleAssetsLiquidityCurrent': 200000,
        'ssmt:InvestmentsInSubsidiariesLiquidityCurrent': 500000,
        'ssmt:InvestmentsInAssociatesLiquidityCurrent': 0,
        'ssmt:InvestmentsInJointVenturesLiquidityCurrent': 0,
        'ssmt:LongTermInvestmentsLiquidityCurrent': 100000,
        'ssmt:DeferredTaxAssetsLiquidityCurrent': 75000,
        'ssmt:OtherAssetsLiquidityCurrent': 25000,
        'ssmt:BankOverdraftsLiquidityCurrent': 150000,
        'ssmt:TradePayablesLiquidityCurrent': 650000,
        'ssmt:OtherPayablesLiquidityCurrent': 200000,
        'ssmt:ShortTermBorrowingsLiquidityCurrent': 500000,
        'ssmt:CurrentTaxLiabilitiesLiquidityCurrent': 120000,
        'ssmt:ShortTermProvisionsLiquidityCurrent': 80000,
        'ssmt:LongTermBorrowingsLiquidityCurrent': 1200000,
        'ssmt:DeferredTaxLiabilitiesLiquidityCurrent': 100000,
        'ssmt:LongTermProvisionsLiquidityCurrent': 75000,
        'ssmt:OtherLiabilitiesLiquidityCurrent': 25000,
        'ssmt:ShareCapitalLiquidityCurrent': 1000000,
        'ssmt:SharePremiumLiquidityCurrent': 500000,
        'ssmt:RetainedEarningsLiquidityCurrent': 1725000,
        'ssmt:OtherReservesLiquidityCurrent': 200000,
        // Previous Year (10% lower)
        'ssmt:CashAndCashEquivalentsLiquidityPrevious': 765000,
        'ssmt:ShortTermInvestmentsLiquidityPrevious': 180000,
        'ssmt:TradeReceivablesLiquidityPrevious': 1080000,
        'ssmt:OtherReceivablesLiquidityPrevious': 135000,
        'ssmt:InventoriesLiquidityPrevious': 855000,
        'ssmt:PrepaymentsLiquidityPrevious': 45000,
        'ssmt:PropertyPlantAndEquipmentLiquidityPrevious': 3150000,
        'ssmt:InvestmentPropertiesLiquidityPrevious': 0,
        'ssmt:IntangibleAssetsLiquidityPrevious': 180000,
        'ssmt:InvestmentsInSubsidiariesLiquidityPrevious': 450000,
        'ssmt:InvestmentsInAssociatesLiquidityPrevious': 0,
        'ssmt:InvestmentsInJointVenturesLiquidityPrevious': 0,
        'ssmt:LongTermInvestmentsLiquidityPrevious': 90000,
        'ssmt:DeferredTaxAssetsLiquidityPrevious': 67500,
        'ssmt:OtherAssetsLiquidityPrevious': 22500,
        'ssmt:BankOverdraftsLiquidityPrevious': 135000,
        'ssmt:TradePayablesLiquidityPrevious': 585000,
        'ssmt:OtherPayablesLiquidityPrevious': 180000,
        'ssmt:ShortTermBorrowingsLiquidityPrevious': 450000,
        'ssmt:CurrentTaxLiabilitiesLiquidityPrevious': 108000,
        'ssmt:ShortTermProvisionsLiquidityPrevious': 72000,
        'ssmt:LongTermBorrowingsLiquidityPrevious': 1080000,
        'ssmt:DeferredTaxLiabilitiesLiquidityPrevious': 90000,
        'ssmt:LongTermProvisionsLiquidityPrevious': 67500,
        'ssmt:OtherLiabilitiesLiquidityPrevious': 22500,
        'ssmt:ShareCapitalLiquidityPrevious': 1000000,
        'ssmt:SharePremiumLiquidityPrevious': 500000,
        'ssmt:RetainedEarningsLiquidityPrevious': 1372500,
        'ssmt:OtherReservesLiquidityPrevious': 180000
      });
    } else {
      // 1 column: Current only (no consolidation, no previous year)
      Object.assign(demoData, {
        'ssmt:CashAndCashEquivalentsLiquidityCurrent': 850000,
        'ssmt:ShortTermInvestmentsLiquidityCurrent': 200000,
        'ssmt:TradeReceivablesLiquidityCurrent': 1200000,
        'ssmt:OtherReceivablesLiquidityCurrent': 150000,
        'ssmt:InventoriesLiquidityCurrent': 950000,
        'ssmt:PrepaymentsLiquidityCurrent': 50000,
        'ssmt:PropertyPlantAndEquipmentLiquidityCurrent': 3500000,
        'ssmt:InvestmentPropertiesLiquidityCurrent': 0,
        'ssmt:IntangibleAssetsLiquidityCurrent': 200000,
        'ssmt:InvestmentsInSubsidiariesLiquidityCurrent': 500000,
        'ssmt:InvestmentsInAssociatesLiquidityCurrent': 0,
        'ssmt:InvestmentsInJointVenturesLiquidityCurrent': 0,
        'ssmt:LongTermInvestmentsLiquidityCurrent': 100000,
        'ssmt:DeferredTaxAssetsLiquidityCurrent': 75000,
        'ssmt:OtherAssetsLiquidityCurrent': 25000,
        'ssmt:BankOverdraftsLiquidityCurrent': 150000,
        'ssmt:TradePayablesLiquidityCurrent': 650000,
        'ssmt:OtherPayablesLiquidityCurrent': 200000,
        'ssmt:ShortTermBorrowingsLiquidityCurrent': 500000,
        'ssmt:CurrentTaxLiabilitiesLiquidityCurrent': 120000,
        'ssmt:ShortTermProvisionsLiquidityCurrent': 80000,
        'ssmt:LongTermBorrowingsLiquidityCurrent': 1200000,
        'ssmt:DeferredTaxLiabilitiesLiquidityCurrent': 100000,
        'ssmt:LongTermProvisionsLiquidityCurrent': 75000,
        'ssmt:OtherLiabilitiesLiquidityCurrent': 25000,
        'ssmt:ShareCapitalLiquidityCurrent': 1000000,
        'ssmt:SharePremiumLiquidityCurrent': 500000,
        'ssmt:RetainedEarningsLiquidityCurrent': 1725000,
        'ssmt:OtherReservesLiquidityCurrent': 200000
      });
    }

    this.statementOfFinancialPositionLiquidityForm.patchValue(demoData);
    this.calculateTotals();

    let message = 'Sample Current Year data loaded (liquidity order)';
    if (this.isConsolidated && this.hasPreviousYear) {
      message = 'Sample Group/Company and Current/Previous Year data loaded (liquidity order)';
    } else if (this.isConsolidated) {
      message = 'Sample Group and Company data loaded (liquidity order - Current Year only)';
    } else if (this.hasPreviousYear) {
      message = 'Sample Current and Previous Year data loaded (liquidity order)';
    }

    this.messageService.add({
      severity: 'success',
      summary: 'Demo Data Loaded',
      detail: message,
      life: TOAST_DURATION_DEFAULT_MS
    });
  }

  /**
   * Clear form
   */
  clearForm(): void {
    this.statementOfFinancialPositionLiquidityForm.reset();
    this.messageService.add({
      severity: 'info',
      summary: 'Form Cleared',
      detail: 'All fields have been reset',
      life: TOAST_DURATION_DEFAULT_MS
    });
  }

  /**
   * Submit form
   */
  submitForm(): void {
    if (this.reloadPromptDismissed()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Reload required',
        detail: 'Please refresh the page to obtain the latest version of this sheet.',
        life: TOAST_DURATION_LONG_MS
      });
      return;
    }

    if (this.statementOfFinancialPositionLiquidityForm.invalid) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please fill in all required fields correctly',
        life: TOAST_DURATION_LONG_MS
      });
      return;
    }

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('Form submitted:', this.statementOfFinancialPositionLiquidityForm.value);
    this.messageService.add({
      severity: 'success',
      summary: 'Form Submitted',
      detail: 'Statement of financial position (liquidity) data saved successfully',
      life: TOAST_DURATION_DEFAULT_MS
    });
  }

  /**
   * Check if a control is a section header
   */
  isSectionHeader(control: FormControlSchema): boolean {
    return control.type === 'SectionHeader';
  }

  /**
   * Check if a field is calculated
   */
  /**
   * Does this row's label get a `*`?
   *
   * Delegates to the shared `isControlMandatory` so this sheet cannot drift from
   * the other 16 — the marker used to live only inside
   * `<label class="field-label">`, which this sheet does not render, so its
   * mandatory rows went unmarked from the initial scaffolding until 2026-08-11.
   * See `isControlMandatory` in `required-field.util.ts`.
   *
   * `crossSheetValue` is supplied because a cross-sheet `requiredIf` degrades to
   * "not mandatory" without it; every other declaration form this sheet uses is
   * unconditional and needs no context. Same-sheet `selfData` is deliberately
   * NOT passed: this sheet keys its values per cell
   * (`qname[Dimension][Period]`), not by bare qname, so a same-sheet lookup
   * would silently miss. Add it — with the right key shape — if a same-sheet
   * conditional rule is ever declared here.
   */
  isFieldRequired(control: any): boolean {
    return isControlMandatory(control, {
      crossSheetValue: (sheet, field) =>
        (this.stateService.getSheetData(sheet as any) as any)?.[field],
    });
  }

  isCalculatedField(control: FormControlSchema): boolean {
    return control.ui.calculated === true;
  }

  /**
   * Row-type guard: a monetary, dual-column line item (the rows that own an
   * Error Description cell). Used by both the template and getRowErrorDescription.
   */
  isMonetaryItem(control: FormControlSchema): boolean {
    return control.ui.control === 'number' && control.ui.dualColumn === true;
  }

  // Developer Note (TODO): extracted-sum warning wired to the shared service. The
  // warning is currently DORMANT in production — role10's N8N snapshot is not yet
  // seeded by filing-form-container.populateSnapshotFromMbrsData and BE must return
  // snapshot data for this sheet.
  // (Mapping Status badge column intentionally NOT added here — Error Description only.)
  private validateAgainstApiData(): void {
    const visible = resolveRole10VisibleSuffixes(this.stateService.getSheetData(SHEET_KEYS.scopeOfFiling));
    const columns = this.ROLE10_SUFFIX_LABELS.filter((column) => visible.includes(column.key as never));

    const warnings = this.extractedSumWarning.computeMismatchWarnings({
      sheetKey: SHEET_KEYS.statementOfFinancialPositionLiquidity,
      controls: this.schema.controls,
      columns,
      getControlName: (qname, key) => qname + key,
      getCurrentValue: (name) => this.statementOfFinancialPositionLiquidityForm.get(name)?.value,
    });

    this.extractedSumWarning.mergeIntoValidationErrors(this.validationErrors, this.schema.controls, warnings);
  }

  /**
   * Render the Error Description cell for a monetary row. role10 only produces
   * suggested-change warnings today; the red-error vs suggested-change split is
   * kept identical to role8 for forward-compat (when real errors land here).
   */
  getRowErrorDescription(control: FormControlSchema): string {
    if (!this.isMonetaryItem(control)) return '';
    const all = this.validationErrors.get(control.qname) || [];
    const suggestedChanges = all.filter((message) => message.includes(EXTRACTED_SUM_MARKER));
    const errors = all.filter((message) => !message.includes(EXTRACTED_SUM_MARKER));
    let result = '';
    if (errors.length > 0) {
      result += '<span class="error-text">ERROR</span><br>';
      result += '<span class="error-text">' + errors.map((errorText, errorIndex) => `${errorIndex + 1}. ${errorText}`).join('<br><br>') + '</span>';
    }
    if (suggestedChanges.length > 0) {
      if (result) result += '<br><br>';
      result += '<span class="error-text">*</span><span class="suggested-change-text"> SUGGESTED CHANGE</span><br>';
      result += '<span class="suggested-change-text">' + suggestedChanges.map((changeText, changeIndex) => `${changeIndex + 1}. ${changeText}`).join('<br><br>') + '</span>';
    }

    // Ctrl+Alt+B baseline debug overlay (non-production). Appended to whatever
    // real validation text this row already has (often none for a clean total).
    // baselineDebugForControl returns '' unless this row is a qualifying total.
    // Columns + getControlName rebuilt inline to mirror validateAgainstApiData's
    // computeMismatchWarnings call exactly (same visible suffixes + concat keys).
    if (this.baselineDebug.enabled()) {
      const visible = resolveRole10VisibleSuffixes(this.stateService.getSheetData(SHEET_KEYS.scopeOfFiling));
      const columns = this.ROLE10_SUFFIX_LABELS.filter((column) => visible.includes(column.key as never));
      const baselineInfo = this.extractedSumWarning.baselineDebugForControl(
        SHEET_KEYS.statementOfFinancialPositionLiquidity,
        control as unknown as SummableControl,
        columns,
        (qname, key) => qname + key
      );
      if (baselineInfo) {
        result = result ? `${result}${baselineInfo}` : baselineInfo;
      }
    }

    return result;
  }

  /**
   * Get header CSS class based on level
   */
  getHeaderClass(level?: string): string {
    return level === 'h3' ? 'section-header-major' : 'section-header-minor';
  }

  /**
   * Check if form control has an error
   */
  hasError(fieldName: string): boolean {
    const control = this.statementOfFinancialPositionLiquidityForm.get(fieldName);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  /**
   * Get error message for a form control
   */
  getErrorMessage(fieldName: string): string {
    const control = this.statementOfFinancialPositionLiquidityForm.get(fieldName);
    if (!control) return '';

    if (control.errors?.['required']) {
      return 'This field is required';
    }
    if (control.errors?.['min']) {
      return `Minimum value is ${control.errors['min'].min}`;
    }
    return 'Invalid value';
  }

  /**
   * Memoised per-control numeric policy — this sheet's single declaration
   * point for decimals, grouping and the integer-digit cap, all resolved from
   * the control's own schema record rather than hand-written per binding.
   *
   * Memoised because templates call this once per binding inside an `*ngFor`;
   * an unmemoised `resolveNumericPolicy` would re-resolve on every
   * change-detection cycle. Keyed by `qname`, unique per control in a sheet.
   *
   * `[min]`/`[max]` are deliberately NOT bound from here — `SchemaNumericDirective`
   * assigns them onto the PrimeNG instance directly. See that directive's class
   * docblock for why a template binding coerces an unconstrained `undefined`
   * to `NaN` and silently blocks the minus key on signed monetary fields.
   */
  private policyCache = new Map<string, NumericFieldPolicy>();

  numericPolicy(control: FormControlSchema): NumericFieldPolicy {
    const cached = this.policyCache.get(control.qname);
    if (cached)
      return cached;
    const resolved = resolveNumericPolicy(control);
    this.policyCache.set(control.qname, resolved);
    return resolved;
  }

}
